import React from 'react'
import { Session, DEPARTMENT_COLORS, DEPARTMENT_LABELS, ConciergeMode } from '../types/communication.types'
import { ConciergeModeSelector } from './ConciergeModeSelector'
import { ConciergeModeBadgeCompact } from './ConciergeModeBadge'
import moment from 'moment'

interface SessionCardProps {
  session: Session
  isActive: boolean
  onClick: () => void
  onConciergeModeChange: (sessionKey: string, mode: ConciergeMode) => Promise<void>
  debugInfo?: {
    sessionCount?: number // Para mostrar quantas sessões foram agrupadas
    isDuplicated?: boolean
  }
  aiSuggestionsCount?: number // 🤖 Quantidade de sugestões pendentes
}

export const SessionCard: React.FC<SessionCardProps> = ({
  session,
  isActive,
  onClick,
  onConciergeModeChange,
  debugInfo,
  aiSuggestionsCount = 0
}) => {
  // 🔍 DEBUG: Log do conciergeMode da sessão (ANTES de qualquer return)
  React.useEffect(() => {
    if (session.guestName && session.conciergeMode) {
      console.log(`🎯 [SessionCard] ${session.guestName} - conciergeMode:`, session.conciergeMode);
    }
  }, [session.conciergeMode, session.guestName]);
  
  // 🛡️ VALIDAÇÃO: Não renderizar sessão inválida (APÓS todos os hooks)
  if (!session.sessionKey || !session.guestName || !session.hotelId) {
    console.error('❌ [SessionCard] Tentativa de renderizar sessão INVÁLIDA:', session)
    return null
  }
  
  const departmentColor = DEPARTMENT_COLORS[session.department]
  const departmentLabel = DEPARTMENT_LABELS[session.department]
  
  const formatLastMessageTime = (date: Date) => {
    return moment(date).format('HH:mm')
  }

  const getLastMessagePreview = () => {
    if (!session.lastMessage) return 'Nenhuma mensagem'
    
    const text = session.lastMessage.text
    if (text.length > 40) {
      return text.substring(0, 40) + '...'
    }
    return text
  }

  const handleConciergeModeChange = async (mode: ConciergeMode) => {
    console.log('🎯 [SessionCard] Solicitando mudança de modo:', { 
      sessionKey: session.sessionKey, 
      guestName: session.guestName,
      from: session.conciergeMode,
      to: mode 
    })
    
    try {
      await onConciergeModeChange(session.sessionKey, mode)
      console.log('✅ [SessionCard] Callback onConciergeModeChange executado com sucesso')
    } catch (error) {
      console.error('❌ [SessionCard] Erro no callback onConciergeModeChange:', error)
    }
  }

  return (
    <div
      className={`card cursor-pointer session-card ${isActive ? 'session-card--active' : ''}`}
      onClick={onClick}
      style={{ 
        marginBottom: '8px',
        position: 'relative',
        zIndex: 'auto'
      }}
    >
      <div className="card-body p-4">
        {/* Header com nome e status */}
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div className="d-flex align-items-center">
            {/* Status de conexão */}
            <div
              className={`badge badge-circle badge-sm me-2 ${
                session.connected ? 'badge-success' : 'badge-light'
              }`}
              title={session.connected ? 'Conectado' : 'Desconectado'}
            >
              <span className={`bullet bullet-dot ${
                session.connected ? 'bg-success' : 'bg-gray-400'
              }`}></span>
            </div>

            {/* Nome do hóspede */}
            <h6 className="mb-0 fw-bold text-gray-800">
              {session.guestName}
            </h6>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* Badge de debug para sessões duplicadas */}
            {debugInfo?.isDuplicated && debugInfo.sessionCount && debugInfo.sessionCount > 1 && (
              <span 
                className="badge badge-warning badge-sm"
                title={`Esta sessão foi consolidada de ${debugInfo.sessionCount} sessões duplicadas`}
              >
                ⚠️ {debugInfo.sessionCount}
              </span>
            )}

            {/* 🤖 Badge de sugestões da IA pendentes */}
            {aiSuggestionsCount > 0 && (
              <span 
                className="badge badge-info animate__animated animate__pulse animate__infinite"
                style={{
                  minWidth: '26px',
                  height: '26px',
                  borderRadius: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: '700',
                  padding: '0 8px',
                  boxShadow: '0 2px 4px rgba(13, 202, 240, 0.4)',
                  background: 'linear-gradient(135deg, #0dcaf0 0%, #0d8db8 100%)'
                }}
                title={`${aiSuggestionsCount} sugestão(ões) da IA disponível(eis)`}
              >
                🤖 {aiSuggestionsCount}
              </span>
            )}

            {/* Badge de mensagens não lidas - Destacado */}
            {session.unreadCount !== undefined && session.unreadCount > 0 && (
              <span 
                className="badge badge-danger"
                style={{
                  minWidth: '26px',
                  height: '26px',
                  borderRadius: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '700',
                  padding: '0 9px',
                  boxShadow: '0 2px 4px rgba(241, 65, 108, 0.3)'
                }}
                title={`${session.unreadCount} mensagem${session.unreadCount > 1 ? 'ns' : ''} não lida${session.unreadCount > 1 ? 's' : ''}`}
              >
                {session.unreadCount}
              </span>
            )}
          </div>
        </div>

        {/* Informações do quarto e departamento */}
        <div className="d-flex align-items-center mb-2">
          <span className="text-muted fs-7 me-3">
            <i className="fas fa-bed me-1"></i>
            Quarto {session.room}
          </span>
          
          <span 
            className="badge badge-sm"
            style={{ 
              backgroundColor: departmentColor + '20',
              color: departmentColor,
              border: `1px solid ${departmentColor}40`
            }}
          >
            {departmentLabel}
          </span>

          {/* 🤖 Badge de modo de operação */}
          {session.conciergeMode && (
            <span className="ms-2">
              <ConciergeModeBadgeCompact 
                mode={session.conciergeMode}
                size="sm"
              />
            </span>
          )}
        </div>

        {/* Preview da última mensagem */}
        <div className="mb-2">
          <p className="text-muted fs-7 mb-0 line-clamp-2">
            {getLastMessagePreview()}
          </p>
        </div>

        {/* Seletor de modo de operação */}
        <div className="mb-2" onClick={(e) => e.stopPropagation()}>
          <ConciergeModeSelector
            currentMode={session.conciergeMode}
            onChange={handleConciergeModeChange}
          />
        </div>

        {/* Timestamp da última mensagem */}
        {session.lastMessageAt && (
          <div className="text-end">
            <small className="text-muted fs-8">
              {formatLastMessageTime(session.lastMessageAt)}
            </small>
          </div>
        )}

        {/* Indicador visual de mensagem não lida */}
        {session.unreadCount !== undefined && session.unreadCount > 0 && (
          <div
            className="position-absolute start-0 top-50 translate-middle-y"
            style={{
              width: '4px',
              height: '60%',
              backgroundColor: '#F1416C',
              borderRadius: '0 2px 2px 0'
            }}
          />
        )}
      </div>

      {/* Hover effect */}
      <style>
        {`
          .session-card {
            transition: all 0.2s ease;
            border: 1px solid var(--bs-gray-300);
            position: relative;
          }
          
          .session-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.15);
            border-color: ${departmentColor};
          }
          
          .session-card--active {
            border-color: ${departmentColor} !important;
            background-color: ${departmentColor}10;
            box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.15);
          }
          
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .cursor-pointer {
            cursor: pointer;
          }
        `}
      </style>
    </div>
  )
}